import React, { useCallback, useMemo, type ComponentType } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { useInlineCreateHandlers } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import InlineCreateForm, {
	type Props as InlineCreateFormProps,
} from '@atlassian/jira-issue-table-inline-issue-create/src/ui/inline-create-form/index.tsx';
import InlineCreateTrigger from '@atlassian/jira-issue-table-inline-issue-create/src/ui/inline-create-trigger/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { issueCreateFooter_nativeIssueTable_rankField$key as RankFieldFragment } from '@atlassian/jira-relay/src/__generated__/issueCreateFooter_nativeIssueTable_rankField.graphql.ts';
import type { issueCreateFooter_nativeIssueTable_issues$key } from '@atlassian/jira-relay/src/__generated__/issueCreateFooter_nativeIssueTable_issues.graphql.ts';
import type { ContextualFields } from '@atlassian/jira-issue-table-inline-issue-create/src/common/types.tsx';
import type { BorderWidth, FooterProps, RankFieldTerms } from '../../../common/types.tsx';
import { Footer as DefaultFooter } from '../../../common/ui/components/footer/index.tsx';
import {
	useIsIssueRankDescending,
	useIsIssueRankingEnabled,
	useJql,
	useProjectContext,
} from '../../../controllers/features/selectors.tsx';
import { useCalculateIssueInsertionPoint } from '../../../controllers/table-items-intersection-observer/index.tsx';
import { useIssueCreateMutation } from '../../../services/issue-create-mutation/index.tsx';

export type Props = FooterProps & {
	Footer?: ComponentType<FooterProps>;
	issues: issueCreateFooter_nativeIssueTable_issues$key;
	rankField: RankFieldFragment | null;
};

type FormWithInsertionPointProps = Omit<InlineCreateFormProps, 'onCreateIssue'> & {
	connectionId: string;
	rankFieldTerms: RankFieldTerms;
};

const FormWithInsertionPoint = ({
	connectionId,
	rankFieldId,
	rankFieldTerms,
	...props
}: FormWithInsertionPointProps) => {
	const insertionPoint = useCalculateIssueInsertionPoint();

	let jql: string | undefined;
	if (expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		jql = useJql();
	}

	const handleIssueCreate = useIssueCreateMutation({
		issueConnectionId: connectionId,
		jql,
		...(fg('jsc_list_reranking_gate') && { rankFieldTerms }),
	});

	const isIssueRankDescending = useIsIssueRankDescending();
	const isIssueRankingEnabled = useIsIssueRankingEnabled();

	const formContextualFields = useMemo<ContextualFields | undefined>(() => {
		if (!isIssueRankingEnabled || !fg('jsc_list_reranking_gate')) {
			return undefined;
		}
		if (isIssueRankDescending) {
			return insertionPoint?.position === 'below'
				? { rankBeforeIssueId: insertionPoint?.issueId, rankFieldId }
				: { rankAfterIssueId: insertionPoint?.issueId, rankFieldId };
		}
		return insertionPoint?.position === 'below'
			? { rankAfterIssueId: insertionPoint?.issueId, rankFieldId }
			: { rankBeforeIssueId: insertionPoint?.issueId, rankFieldId };
	}, [
		insertionPoint?.issueId,
		insertionPoint?.position,
		isIssueRankDescending,
		isIssueRankingEnabled,
		rankFieldId,
	]);

	const handleIssueCreateWithInsertionPoint = useCallback<InlineCreateFormProps['onCreateIssue']>(
		(issueToCreate, contextualFields) => {
			if (insertionPoint == null) {
				return handleIssueCreate(issueToCreate, contextualFields);
			}

			return handleIssueCreate(issueToCreate, contextualFields, {
				insertAfter: insertionPoint.position === 'below',
				insertNodeId: insertionPoint.issueId,
			});
		},
		[handleIssueCreate, insertionPoint],
	);
	return (
		<Box xcss={formContainerStyles}>
			<InlineCreateForm
				{...props}
				onCreateIssue={handleIssueCreateWithInsertionPoint}
				rankFieldId={rankFieldId}
				contextualFields={formContextualFields}
			/>
		</Box>
	);
};

export const IssueCreateFooter = ({
	Footer = DefaultFooter,
	issues,
	rankField,
	...footerProps
}: Props) => {
	const issueData = useFragment<issueCreateFooter_nativeIssueTable_issues$key>(
		graphql`
			fragment issueCreateFooter_nativeIssueTable_issues on JiraIssueConnection {
				__id
			}
		`,
		issues,
	);

	const rankFieldData = useFragment<RankFieldFragment>(
		graphql`
			fragment issueCreateFooter_nativeIssueTable_rankField on JiraJqlFieldWithAliases
			@argumentDefinitions(
				isJSCAliasChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-alias-changes.relayprovider"
				}
			) {
				fieldId
				jqlTerm
				aliases @include(if: $isJSCAliasChangesEnabled)
			}
		`,
		rankField ?? null,
	);

	const projectContext = useProjectContext();
	const { isFormVisible, formProps, triggerProps } = useInlineCreateHandlers({
		projectKey: projectContext,
		minHierarchyLevel: 0,
	});
	const rankFieldTerms = useMemo<RankFieldTerms>(
		() => ({
			jqlTerm: rankFieldData?.jqlTerm ?? null,
			aliases: rankFieldData?.aliases ? Array.from(rankFieldData.aliases) : null,
		}),
		[rankFieldData],
	);

	if (isFormVisible) {
		return (
			<FormWithInsertionPoint
				connectionId={issueData?.__id}
				rankFieldId={rankFieldData?.fieldId ?? null}
				rankFieldTerms={rankFieldTerms}
				{...formProps}
				appearance="footer"
				depth={0}
			/>
		);
	}

	return (
		<Footer
			{...footerProps}
			start={
				<Box xcss={triggerContainerStyles}>
					<InlineCreateTrigger {...triggerProps} />
				</Box>
			}
		/>
	);
};

const BORDER_WIDTH: BorderWidth = '1px';
const formContainerStyles = xcss({
	backgroundColor: 'elevation.surface',
	boxShadow: 'elevation.shadow.overflow',
	borderTopWidth: BORDER_WIDTH,
	borderTopStyle: 'solid',
	borderTopColor: 'color.border',
});

const triggerContainerStyles = xcss({
	display: 'flex',
	flex: 1,
	alignItems: 'center',
	minHeight: '32px',
});
